import { Action, Update } from 'history';
import { useEffect, useMemo } from 'react';
import browserHistory from '~/browserHistory';

interface IUseBlockBackNavigationParams {
  blockedRoutePatterns: string[];
  redirectTo: string;
}

/**
 * Hook to prevent back navigation to specific routes by redirecting to a different route.
 * Useful for preventing users from navigating back to creation/edit flows that should be one-way.
 *
 * @param blockedRoutePatterns - Array of route patterns to block (supports :param syntax, e.g., '/users/:id/edit')
 * @param redirectTo - The route to redirect to when a blocked route is detected
 *
 * @example
 * useBlockBackNavigation({
 *   blockedRoutePatterns: [
 *     '/permanent-jobs/postings/:id/create',
 *     '/permanent-jobs/postings/:id/onsite-interviews/create'
 *   ],
 *   redirectTo: `/permanent-jobs/job-overview/${postingId}`
 * });
 *
 * @performance
 * The hook internally memoizes the regex pattern conversion to prevent unnecessary re-renders.
 * However, for optimal performance, consider memoizing the blockedRoutePatterns array:
 *
 * @example
 * // Good - memoized patterns
 * const blockedPatterns = useMemo(() => [
 *   '/permanent-jobs/postings/:id/create',
 *   '/permanent-jobs/postings/:id/onsite-interviews/create'
 * ], []);
 *
 * useBlockBackNavigation({
 *   blockedRoutePatterns: blockedPatterns,
 *   redirectTo: `/permanent-jobs/job-overview/${postingId}`
 * });
 */
export const useBlockBackNavigation = ({
  blockedRoutePatterns,
  redirectTo,
}: IUseBlockBackNavigationParams): void => {
  // Memoize the regex patterns to avoid re-computation on every render
  // Use a stable key based on the patterns array content
  const patternsKey = blockedRoutePatterns.join('|');
  const regexPatterns = useMemo(() => {
    // Convert route patterns to regex patterns
    // e.g., '/users/:id/edit' becomes /^\/users\/[^/]+\/edit$/
    return blockedRoutePatterns.map((pattern) => {
      const regexPattern = pattern
        .replace(/:[^/]+/g, '[^/]+') // Replace :param with regex to match any non-slash characters
        .replace(/\//g, '\\/'); // Escape forward slashes
      return new RegExp(`^${regexPattern}$`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patternsKey]);

  useEffect(() => {

    // Listen to history changes
    const unlisten = browserHistory.listen(({ action, location }: Update) => {
      // Only intercept back/forward navigation (POP action)
      if (action === Action.Pop) {
        const pathname = location.pathname;

        // Check if the destination matches any blocked pattern
        const isBlocked = regexPatterns.some((regex) => regex.test(pathname));

        if (isBlocked) {
          // Redirect to the specified route
          browserHistory.replace(redirectTo);
        }
      }
    });

    // Cleanup listener on unmount
    return () => {
      unlisten();
    };
  }, [regexPatterns, redirectTo]);
};
